// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import {
  Stack,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  Select,
  Collapse,
  CardMedia,
  Divider,
} from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";
import { SmartNumberInput } from "../../../components/SmartInputs";

// theme
import { B1Tiny, B3Tiny, ModalActionsLoadingButton, ModalContentOutlinedInput, ModalContentMenuItem, ModalAlertError, ModalContentSwitch } from "../../../theme/styled";

const TAKE_PROFIT_ACTIVATION_POSITION_HELP_IMAGE = null;
const TAKE_PROFIT_ACTIVATION_POSITION_HELP_DESCRIPTION = (
  <>
    Similar to the stop loss trailing feature, it is possible to configure up to 50 dynamic take profit ranges. A range is defined by its activation
    position. The activation position is a reference to the initial stop loss distance, which is defined by the difference between the first stop loss
    and the trade opening price. The initial stop loss difference is always a positive value irrespective of whether a long or short trade is
    executed. This initial value represents the unitless reference of 100. 0 is exactly the price the initial stop loss was set to and 100 is the
    opening price of the order. Interpolating this principle, every price above (long) or below (short) the opening price is a value above 100. By
    using this principle it is possible to treat a trade entirely independent from fixed values such as pip count.
    <br />
    <br />
    So, when setting for example 170 as activation position, this means that when the price is increasing (long) or decreasing (short) a significant
    amount, it will activate the configured dynamic take profit stage.{" "}
    <b>A dynamic take profit stage is active until the end of the trade or if another stage is activated later on. </b>Based on this logic, an
    activation position must be between 0 and the actually initially configured initial take profit (e.g. 240 in case of a 1:4 risk ratio).
  </>
);

const TAKE_PROFIT_TYPES_HELP_SECTION = {
  0: {
    image: null,
    description: (
      <>
        A stage configured as <b>Off</b> disables dynamic modification of the take profit level. This option allows you to deactivate take profit
        adjustments at specific points during a trade. For example, after a successful take profit modification, you may want to prevent further
        changes to the take profit level by activating an Off-stage.
      </>
    ), // "type-off",
    imageHeight: "450px",
  },
  1: {
    image: "/illustrations/converted/take_profit_dynamics/type-linear.png",
    description: (
      <>
        The linear increase/decrease dynamic take profit method increases/decreases the take profit after each bar in the currently active timeframe,
        resulting in a strictly linear rise/fall in the take profit value. The initial take profit distance is determined by the difference between
        the entry price and the initial take profit level. This distance represents the base value of 100%. The only input parameter for this
        algorithm is the step size, defined by <b>Linear step</b>.<br />
        <br />
        A value of 50 means the take profit will increase by 0.5% of the initial take profit distance after each closed bar. For instance, if the
        initial take profit distance was 1000 pips, the take profit level will increase/decrease by 5 pips after every closed bar. The increment might
        be small in some timeframes and initial take profit configurations, so the adjustment could be smaller than the minimum increment for
        modifying the stop loss.
        <br />
        <br />
        <b>
          If you plan to use this feature in live trading systems, using it with a trailing stop loss is strongly recommended. This ensures a balanced
          and effective trading strategy.
        </b>
      </>
    ),
    imageHeight: "279px",
  },
  2: {
    image: null,
    description: (
      <>
        The dynamic increase/decrease dynamic take profit method adjusts the required distance between the current price and take profit level over
        time. It starts with an initial target distance and modifies this distance after each completed bar in the active timeframe.
        <br />
        <br />
        The behavior depends on whether the <b>Changing difference</b> parameter is positive or negative:
        <ul>
          <li>
            <b>Positive values</b>: The target distance decreases with each bar, causing the take profit to move closer to the current price over
            time. This creates an accelerating effect where the take profit becomes easier to hit as time passes.
          </li>
          <li>
            <b>Negative values</b>: The target distance increases with each bar, causing the take profit to move further from the current price over
            time. This makes the take profit progressively harder to reach.
          </li>
        </ul>
        For buy trades, the system compares the take profit level minus the current ask price against the target distance. For sell trades, it
        compares the current bid price minus the take profit level. When the actual distance deviates from the target distance, the take profit
        adjusts to maintain exactly the target distance from the current price.
        <br />
        <br />
        This creates a dynamic system where the take profit behavior changes progressively with each completed bar, either becoming more conservative
        (negative values) or more aggressive (positive values) over time.
        <br />
        <br />
        <b>
          If you plan to use this feature in live trading systems, using it with a trailing stop loss is strongly recommended. This ensures a balanced
          and effective trading strategy.
        </b>
      </>
    ),
    imageHeight: "450px",
  },
  3: {
    image: "/illustrations/converted/take_profit_dynamics/type-market.png",
    description: (
      <>
        This algorithm allows you to instruct a trading module to adjust the take profit level in response to rapidly fluctuating prices, potentially
        increasing the profit per trade. The algorithm can be configured using four input parameters. While the default parameters provide a robust
        and responsive configuration for dynamic take profit adjustment, you can customize the algorithm using the following parameters:
        <ul>
          <li>
            <b>Market maximum bars:</b> This parameter defines the time range to detect a rapid price movement. A smaller value means the price must
            change more quickly to trigger dynamic take profit modification.
          </li>
          <li>
            <b>Market M1 position:</b> This parameter sets the upper threshold that must be exceeded to activate dynamic movement. Dynamic movement is
            only unlocked when the Market M1 and Market M2 levels are surpassed within the defined period measured by bars.{" "}
          </li>
          <li>
            <b>Market M2 position:</b> This parameter sets the lower threshold that must be exceeded to activate dynamic movement. As with Market M1,
            dynamic movement is only unlocked when both thresholds are surpassed.
          </li>
          <li>
            <b>Market end postion:</b> This parameter sets the threshold that the price must pass to turn off dynamic movement.
          </li>
        </ul>
        All input parameters use the initial take profit difference as the 100% reference price difference. Please refer to the following function
        principle for further clarification.
      </>
    ),
    imageHeight: "331px",
  },
  4: {
    image: null,
    description: (
      <>
        This dynamic take profit method enables the trading module to use an indicator value for calculating the take profit level. Only indicators
        configured with <b>Forward</b> for <b>Signal used for</b> can be selected. This method is particularly useful when implementing a custom
        algorithm to determine take profit levels, which can be achieved by configuring a custom indicator or custom oscillator.
        <br />
        <br />
        When using predefined indicator algorithms, a distinction is made between indicators that move along the chart and oscillators that derive
        values around a fixed reference. For indicators, the value is used directly as the take profit level. For oscillators, the value is added to
        (long) or subtracted (short) from the current price level to determine the take profit position.
        <br />
        <br />
        To modify the multiplier when using an oscillator, configure the <b>Forward adjustment</b> in your indicator settings.
      </>
    ),
    imageHeight: "450px",
  },
  5: {
    image: null,
    description: (
      <>
        A chart analyzer signal generator provides a powerful method for detecting support and resistance levels. These levels often represent optimal
        values for take profit placement. This option enables the trading algorithm to automatically select one of the forwarded resistance or support
        levels as the take profit target.
        <br />
        <br />
        Only chart analyzers configured with <b>Forward</b> for <b>Signal used for</b> can be selected. To filter and select only specific support and
        resistance values, configure the <b>Supports and resistances forward option</b> in your chart analyzer settings.
        <br />
        <br />
        In some cases, the forwarded resistance or support level may be too far away or too close to the current price. To define a valid range, you
        can use the following parameters:
        <ul>
          <li>Support/resistance fix pips minimum</li>
          <li>Support/resistance rate of change minimum</li>
          <li>Support/resistance fix pips max</li>
          <li>Support/resistance rate of change max</li>
        </ul>
        Only if the support or resistance lies within these limits will it be selected as the take profit target. In contrast to selecting the initial
        take profit level using a chart analyzer,{" "}
        <b>the dynamic take profit will switch to a closer take profit level when a respective support or resistance occurs</b>.
      </>
    ),
    imageHeight: "450px",
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TakeProfitDynamicsDefaults = {
  nType: "0",
  nChangePosition: "0",
  nLinearStep: "5",
  nChangingDifference: "25",
  nMarketBarsMax: "4",
  nMarketM1: "40",
  nMarketM2: "80",
  nMarketEnd: "25",
  nInosUsedForValueInjection: "0",
  nCaSupResUsedForValueInjection: "0",
  bCaSupResFixPipsMin: false,
  nCaSupResFixPipsMin: "4",
  bCaSupResRateOfChangeMin: false,
  dCaSupResRateOfChangeMin: "0.00000100",
  bCaSupResFixPipsMax: false,
  nCaSupResFixPipsMax: "400",
  bCaSupResRateOfChangeMax: false,
  dCaSupResRateOfChangeMax: "0.00001000",
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
const DEFAULT_LIMITS = {
  nChangePosition: {
    min: -1,
  },
  nLinearStep: {
    step: 1,
  },
  nChangingDifference: {
    min: 1,
  },
  nMarketBarsMax: {
    min: 0,
  },
  nMarketM1: {
    min: 0,
    max: 100,
  },
  nMarketM2: {
    min: 0,
    max: 100,
  },
  nMarketEnd: {
    min: 0,
    max: 100,
  },
  nCaSupResFixPipsMin: {
    min: 0,
  },
  dCaSupResRateOfChangeMin: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
  nCaSupResFixPipsMax: {
    min: 0,
  },
  dCaSupResRateOfChangeMax: {
    digits: 8,
    step: 0.00001,
    min: 0.00000001,
  },
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const getTypeDisplay = (value) => {
  const types = {
    0: "Off",
    1: "Linear increase/decrease",
    2: "Dynamic increase/decrease",
    3: "Market",
    4: "Indicator value",
    5: "Support/resistance from chart analyzer",
  };
  return types[value] || "Off";
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TakeProfitDynamicsBuildName = (config) => `${getTypeDisplay(config.nType)}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const TakeProfitDynamicsBuildSubtitle = (config) => `Activation position: ${config.nChangePosition}`;

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function TakeProfitDynamicsRenderParameters(
  selectedTakeProfitDynamics,
  getTakeProfitDynamicsConfig,
  professionalMode,
  indicators,
  chartAnalyzers
) {
  const config = getTakeProfitDynamicsConfig(selectedTakeProfitDynamics);

  if (!config) {
    return (
      <Typography variant="contentMiddle" align="center">
        No configuration saved yet!
      </Typography>
    );
  }

  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get indicator name by ID
  const getIndicatorNameById = (indicatorId) => {
    if (!indicatorId || indicatorId === "0") return "None selected";
    if (!indicators || indicators.length === 0) return "No indicators available";

    const indicator = indicators.find((ind) => ind.id.toString() === indicatorId.toString());
    return indicator ? `${indicator.name} (${indicator.subtitle})` : `Unknown indicator (ID: ${indicatorId})`;
  };

  // Helper function to get chart analyzer name by ID
  const getChartAnalyzerNameById = (chartAnalyzerId) => {
    if (!chartAnalyzerId || chartAnalyzerId === "0") return "None selected";
    if (!chartAnalyzers || chartAnalyzers.length === 0) return "No chart analyzers available";

    const chartAnalyzer = chartAnalyzers.find((ca) => ca.id.toString() === chartAnalyzerId.toString());
    return chartAnalyzer ? `${chartAnalyzer.name} (${chartAnalyzer.subtitle})` : `Unknown chart analyzer (ID: ${chartAnalyzerId})`;
  };

  return (
    <Stack spacing={0.5}>
      {renderParam("Activation position", config.nChangePosition)}
      {renderParam("Type", getTypeDisplay(config.nType))}

      {config.nType === "1" && renderParam("Linear step", config.nLinearStep)}
      {config.nType === "2" && renderParam("Changing difference", config.nChangingDifference)}

      {config.nType === "3" && (
        <>
          {renderParam("Market maximum bars", config.nMarketBarsMax)}
          {renderParam("Market M1 position", config.nMarketM1)}
          {renderParam("Market M2 position", config.nMarketM2)}
          {renderParam("Market end postion", config.nMarketEnd)}
        </>
      )}

      {config.nType === "4" && renderParam("Used indicator", getIndicatorNameById(config.nInosUsedForValueInjection))}

      {config.nType === "5" && (
        <>
          {renderParam("Used chart analyzer", getChartAnalyzerNameById(config.nCaSupResUsedForValueInjection))}
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          {config.bCaSupResFixPipsMin === true
            ? renderParam("Support/resistance fix pips minimum", config.nCaSupResFixPipsMin)
            : renderParam("Support/resistance fix pips minimum", "Off")}
          {config.bCaSupResRateOfChangeMin === true
            ? renderParam("Support/resistance rate of change minimum", config.dCaSupResRateOfChangeMin)
            : renderParam("Support/resistance rate of change minimum", "Off")}
          <Divider sx={{ mt: 0, mb: 0, borderColor: "#C8C8C8" }} />
          {config.bCaSupResFixPipsMax === true
            ? renderParam("Support/resistance fix pips maximum", config.nCaSupResFixPipsMax)
            : renderParam("Support/resistance fix pips maximum", "Off")}
          {config.bCaSupResRateOfChangeMax === true
            ? renderParam("Support/resistance rate of change maximum", config.dCaSupResRateOfChangeMax)
            : renderParam("Support/resistance rate of change maximum", "Off")}
        </>
      )}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const TakeProfitDynamicsConfigure = forwardRef(
  ({ onSave, initialData, professionalMode, indicators, chartAnalyzers, initialTakeProfit }, ref) => {
    // ----------------------------------------------------------------------
    // Properties
    // ----------------------------------------------------------------------
    TakeProfitDynamicsConfigure.propTypes = {
      onSave: PropTypes.func.isRequired,
      initialData: PropTypes.object,
      professionalMode: PropTypes.any,
      indicators: PropTypes.array,
      chartAnalyzers: PropTypes.array,
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const [scroll, setScroll] = useState("paper");
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);
    const autoCloseTimeoutRef = useRef(null);

    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warning, setWarning] = useState(false);
    const [returnMessage, setReturnMessage] = useState("");

    const [collapseActivation, setCollapseActivation] = useState(false);
    const [collapseType, setCollapseType] = useState(false);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      openDialog: () => {
        setOpen(true);
      },
      closeDialog: () => {
        setOpen(false);
      },
    }));

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleReset = () => {
      // Clear all errors and touched state
      setErrors({});
      setTouched({});
      // Clear cross-validation error state
      setError(false);
      setReturnMessage("");

      const defaults = {
        ...TakeProfitDynamicsDefaults,
        nType: values.nType, // Keep the current selection
      };

      // Apply all the default values to the form
      Object.keys(defaults).forEach((key) => {
        setFieldValue(key, defaults[key]);
      });
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClose = () => {
      // Clear any existing auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      setOpen(false);
      setError(false);
      setSuccess(false);
      setWarning(false);
      setReturnMessage("");
      resetForm();
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const formik = useFormik({
      initialValues: {
        nType: initialData?.nType ?? TakeProfitDynamicsDefaults.nType,
        nChangePosition: initialData?.nChangePosition ?? TakeProfitDynamicsDefaults.nChangePosition,
        nLinearStep: initialData?.nLinearStep ?? TakeProfitDynamicsDefaults.nLinearStep,
        nChangingDifference: initialData?.nChangingDifference ?? TakeProfitDynamicsDefaults.nChangingDifference,
        nMarketBarsMax: initialData?.nMarketBarsMax ?? TakeProfitDynamicsDefaults.nMarketBarsMax,
        nMarketM1: initialData?.nMarketM1 ?? TakeProfitDynamicsDefaults.nMarketM1,
        nMarketM2: initialData?.nMarketM2 ?? TakeProfitDynamicsDefaults.nMarketM2,
        nMarketEnd: initialData?.nMarketEnd ?? TakeProfitDynamicsDefaults.nMarketEnd,
        nInosUsedForValueInjection: initialData?.nInosUsedForValueInjection ?? TakeProfitDynamicsDefaults.nInosUsedForValueInjection,
        nCaSupResUsedForValueInjection: initialData?.nCaSupResUsedForValueInjection ?? TakeProfitDynamicsDefaults.nCaSupResUsedForValueInjection,
        bCaSupResFixPipsMin: initialData?.bCaSupResFixPipsMin ?? TakeProfitDynamicsDefaults.bCaSupResFixPipsMin,
        nCaSupResFixPipsMin: initialData?.nCaSupResFixPipsMin ?? TakeProfitDynamicsDefaults.nCaSupResFixPipsMin,
        bCaSupResRateOfChangeMin: initialData?.bCaSupResRateOfChangeMin ?? TakeProfitDynamicsDefaults.bCaSupResRateOfChangeMin,
        dCaSupResRateOfChangeMin: initialData?.dCaSupResRateOfChangeMin ?? TakeProfitDynamicsDefaults.dCaSupResRateOfChangeMin,
        bCaSupResFixPipsMax: initialData?.bCaSupResFixPipsMax ?? TakeProfitDynamicsDefaults.bCaSupResFixPipsMax,
        nCaSupResFixPipsMax: initialData?.nCaSupResFixPipsMax ?? TakeProfitDynamicsDefaults.nCaSupResFixPipsMax,
        bCaSupResRateOfChangeMax: initialData?.bCaSupResRateOfChangeMax ?? TakeProfitDynamicsDefaults.bCaSupResRateOfChangeMax,
        dCaSupResRateOfChangeMax: initialData?.dCaSupResRateOfChangeMax ?? TakeProfitDynamicsDefaults.dCaSupResRateOfChangeMax,
      },
      enableReinitialize: true,
      validateOnChange: false,
      validateOnBlur: false,
      onSubmit: (values, { setSubmitting }) => {
        setTimeout(() => {
          try {
            onSave(values);
            setSuccess(true);
            setReturnMessage("Configuration saved successfully!");
            // autoCloseTimeoutRef.current = setTimeout(() => {
            //  handleClose();
            // }, 1500);
          } catch (err) {
            setError(true);
            setReturnMessage("Failed to save configuration.");
            console.error("Save error:", err);
          }
          setSubmitting(false);
        }, 100);
      },
    });

    const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } =
      formik;

    // ----------------------------------------------------------------------
    // Cross-validation logic
    // ----------------------------------------------------------------------
    useEffect(() => {
      // Only perform cross-validation when Type is "Market" (value "3")
      if (values.nType === "3") {
        const nMarketM1 = parseFloat(values.nMarketM1);
        const nMarketM2 = parseFloat(values.nMarketM2);
        const nMarketEnd = parseFloat(values.nMarketEnd);

        if (!Number.isNaN(nMarketM1) && !Number.isNaN(nMarketM2) && !Number.isNaN(nMarketEnd)) {
          // Check if nMarketM2 <= nMarketM1
          if (nMarketM2 <= nMarketM1) {
            setError(true);
            setReturnMessage(
              <>
                <b>Market M2 position</b> cannot be smaller or equal to <b>Market M1 position</b>!
              </>
            );
            return;
          }

          // Check if nMarketEnd <= (100 - nMarketM2)
          if (nMarketEnd <= 100 - nMarketM2) {
            setError(true);
            setReturnMessage(
              <>
                <b>Market end position</b> cannot be smaller or equal to <b>100 - Market M2 position</b>!
              </>
            );
            return;
          }
        }
      } else if (values.nType === "5") {
        // For non-Market types, only validate chart analyzer fields if Type is "5"
        const nCaSupResFixPipsMin = parseFloat(values.nCaSupResFixPipsMin);
        const dCaSupResRateOfChangeMin = parseFloat(values.dCaSupResRateOfChangeMin);
        const nCaSupResFixPipsMax = parseFloat(values.nCaSupResFixPipsMax);
        const dCaSupResRateOfChangeMax = parseFloat(values.dCaSupResRateOfChangeMax);

        // Check chart analyzer fix pips min vs max
        if (values.bCaSupResFixPipsMax === true && values.bCaSupResFixPipsMin === true && !Number.isNaN(nCaSupResFixPipsMin) && !Number.isNaN(nCaSupResFixPipsMax)) {
          if (nCaSupResFixPipsMax <= nCaSupResFixPipsMin) {
            setError(true);
            setReturnMessage(
              <>
                <b>Support/resistance fix pips maximum</b> cannot be smaller or equal to{" "}
                <b>Support/resistance fix pips minimum</b>!
              </>
            );
            return;
          }
        }

        // Check chart analyzer rate of change min vs max
        if (values.bCaSupResRateOfChangeMax === true && values.bCaSupResRateOfChangeMin === true && !Number.isNaN(dCaSupResRateOfChangeMin) && !Number.isNaN(dCaSupResRateOfChangeMax)) {
          if (dCaSupResRateOfChangeMax <= dCaSupResRateOfChangeMin) {
            setError(true);
            setReturnMessage(
              <>
                <b>Support/resistance rate of change maximum</b> cannot be smaller or equal to{" "}
                <b>Support/resistance rate of change minimum</b>!
              </>
            );
            return;
          }
        }
      }

      // If all validations pass, clear error state
      setError(false);
      setReturnMessage("");
    }, [
      values.nType,
      values.nMarketM1,
      values.nMarketM2,
      values.nMarketEnd,
      values.bCaSupResFixPipsMin,
      values.nCaSupResFixPipsMin,
      values.bCaSupResRateOfChangeMin,
      values.dCaSupResRateOfChangeMin,
      values.bCaSupResFixPipsMax,
      values.nCaSupResFixPipsMax,
      values.bCaSupResRateOfChangeMax,
      values.dCaSupResRateOfChangeMax,
    ]);

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    const handleClickOpen = (scrollType) => () => {
      setOpen(true);
      setScroll(scrollType);
    };

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    let showWarning = false;
    if (initialTakeProfit.nSelection === "0") {
      showWarning = true;
    }

    // ----------------------------------------------------------------------
    // Section
    // ----------------------------------------------------------------------
    let saveAndCloseDisabled = false;
    if (values.nType === "4" && indicators && indicators.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nType === "4" &&
      (values.nInosUsedForValueInjection === "" || values.nInosUsedForValueInjection === "0" || values.nInosUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }
    if (values.nType === "5" && chartAnalyzers && chartAnalyzers.length === 0) {
      saveAndCloseDisabled = true;
    }
    if (
      values.nType === "5" &&
      (values.nCaSupResUsedForValueInjection === "" || values.nCaSupResUsedForValueInjection === "0" || values.nCaSupResUsedForValueInjection === 0)
    ) {
      saveAndCloseDisabled = true;
    }

    const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
    if (hasValidationErrors()) {
      saveAndCloseDisabled = true;
    }

    // Disable save button if cross-validation fails
    if (error) {
      saveAndCloseDisabled = true;
    }

    // ----------------------------------------------------------------------
    // Main component
    // ----------------------------------------------------------------------
    return (
      <>
        <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
          Configure dynamic take profit
        </B1Tiny>

        <ModalDialog
          open={open}
          handleClose={handleClose}
          scroll={scroll}
          outlet={
            <>
              <DialogTitle
                id="dialog-title"
                sx={{
                  background: "#F2F2F2",
                  textTransform: "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  alignContent: "center",
                  py: 1,
                  pl: 2,
                  pr: 1,
                }}
              >
                <Typography variant="contentBig">Configure dynamic take profit</Typography>

                <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                  <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
                </IconButton>
              </DialogTitle>

              <FormikProvider value={formik}>
                <DialogContent
                  dividers
                  sx={{
                    background: "#F2F2F2",
                    borderTop: "1px solid #C8C8C8",
                    borderBottom: "0px solid #C8C8C8",
                    px: 2,
                    pt: 2,
                    pb: 1,
                    width: "100%",
                    boxSizing: "border-box",
                    overflowY: "scroll",
                    overflowX: "hidden",
                    // Custom scrollbar styling
                    "&::-webkit-scrollbar": {
                      width: "16px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#e0e0e0",
                      borderRadius: "6px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888888",
                      borderRadius: "6px",
                      border: "2px solid #e0e0e0",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#666666",
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: "thin",
                    scrollbarColor: "#888888 #e0e0e0",
                  }}
                >
                  <Stack spacing={2}>
                    {showWarning ? (
                      <Box>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          The dynamic take profit feature is dependent on configuring an initial take profit. If this condition is not met, the
                          feature remains inactive, and no take profit will be applied.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nChangePosition */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Activation position"
                        helpReference="HandleTakeProfitDynamics"
                        helpText=""
                        helpLink=""
                        fieldName="nChangePosition"
                        touched={touched}
                        errors={errors}
                      />
                      <SmartNumberInput textFieldName="nChangePosition" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Help Sections */}
                    {/* ---------------------------------------------------------------------- */}
                    {TAKE_PROFIT_ACTIVATION_POSITION_HELP_DESCRIPTION && (
                      <Box>
                        <B3Tiny
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCollapseActivation(!collapseActivation);
                          }}
                        >
                          {collapseActivation ? "Hide help section" : "Show help section"}
                        </B3Tiny>

                        <Collapse sx={{ p: "3px" }} in={collapseActivation} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                              {TAKE_PROFIT_ACTIVATION_POSITION_HELP_DESCRIPTION}
                              <br />
                            </Typography>
                          </Box>

                          {TAKE_PROFIT_ACTIVATION_POSITION_HELP_IMAGE && (
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={TAKE_PROFIT_ACTIVATION_POSITION_HELP_IMAGE}
                                alt="Dynamic take profit activation position"
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: "450px",
                                }}
                              />
                            </Box>
                          )}
                        </Collapse>
                      </Box>
                    )}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nType */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Type"
                        helpReference="HandleTakeProfitDynamics"
                        helpText=""
                        helpLink=""
                        fieldName="nType"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select {...getFieldProps("nType")} input={<ModalContentOutlinedInput />} error={Boolean(touched.nType && errors.nType)}>
                          <ModalContentMenuItem value="0">Off</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">Linear increase/decrease</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">Dynamic increase/decrease</ModalContentMenuItem>
                          {professionalMode === true || values.nType === "3" ? <ModalContentMenuItem value="3">Market</ModalContentMenuItem> : null}
                          {professionalMode === true || values.nType === "4" ? (
                            <ModalContentMenuItem value="4">Indicator value</ModalContentMenuItem>
                          ) : null}
                          {professionalMode === true || values.nType === "5" ? (
                            <ModalContentMenuItem value="5">Support/resistance from chart analyzer</ModalContentMenuItem>
                          ) : null}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* ---------------------------------------------------------------------- */}
                    {/* Help Sections */}
                    {/* ---------------------------------------------------------------------- */}
                    {TAKE_PROFIT_TYPES_HELP_SECTION[values.nType] && (
                      <Box>
                        <B3Tiny
                          fullWidth
                          variant="outlined"
                          onClick={() => {
                            setCollapseType(!collapseType);
                          }}
                        >
                          {collapseType ? "Hide help section" : "Show help section"}
                        </B3Tiny>

                        <Collapse sx={{ p: "3px" }} in={collapseType} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="contentMiddle" sx={{ mt: 0.5 }}>
                              {TAKE_PROFIT_TYPES_HELP_SECTION[values.nType].description}
                              <br />
                            </Typography>
                          </Box>

                          {TAKE_PROFIT_TYPES_HELP_SECTION[values.nType].image && (
                            <Box
                              sx={{
                                borderRadius: "10px",
                                py: 2,
                                boxShadow: "0px 0px 3px 0px rgba(0,0,0,0.5)",
                                backgroundColor: "white",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={TAKE_PROFIT_TYPES_HELP_SECTION[values.nType].image}
                                alt="Dynamic take profit type"
                                sx={{
                                  objectFit: "contain",
                                  maxHeight: TAKE_PROFIT_TYPES_HELP_SECTION[values.nType].imageHeight,
                                }}
                              />
                            </Box>
                          )}
                        </Collapse>
                      </Box>
                    )}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nLinearStep */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "1" ? (
                      <Box>
                        <SmartParameter
                          value="Linear step"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nLinearStep"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nLinearStep" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nChangingDifference */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "2" ? (
                      <Box>
                        <SmartParameter
                          value="Changing difference"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nChangingDifference"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nChangingDifference" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMarketBarsMax */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market maximum bars"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nMarketBarsMax"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nMarketBarsMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMarketM1 */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market M1 position"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nMarketM1"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nMarketM1" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMarketM2 */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market M2 position"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nMarketM2"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nMarketM2" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nMarketEnd */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "3" ? (
                      <Box>
                        <SmartParameter
                          value="Market end postion"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nMarketEnd"
                          touched={touched}
                          errors={errors}
                        />
                        <SmartNumberInput textFieldName="nMarketEnd" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nInosUsedForValueInjection */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "4" &&
                    (() => {
                      const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                      return forwardIndicators.length > 0;
                    })() ? (
                      <Box>
                        <SmartParameter
                          value="Used indicator"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nInosUsedForValueInjection"
                          touched={touched}
                          errors={errors}
                        />
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nInosUsedForValueInjection")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nInosUsedForValueInjection && errors.nInosUsedForValueInjection)}
                          >
                            <ModalContentMenuItem value="0">
                              <Box>
                                <Typography variant="contentSmall">Please select ...</Typography>
                              </Box>
                            </ModalContentMenuItem>
                            {indicators
                              ?.filter((indicator) => indicator.config?.nSignalUsedFor === "2")
                              .map((indicator) => (
                                <ModalContentMenuItem key={indicator.id} value={indicator.id.toString()}>
                                  <Box>
                                    <Typography variant="contentSmall">
                                      {indicator.name} ({indicator.subtitle})
                                    </Typography>
                                  </Box>
                                </ModalContentMenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    {values.nType === "4" &&
                    (() => {
                      const forwardIndicators = indicators?.filter((ind) => ind.config?.nSignalUsedFor === "2") || [];
                      return forwardIndicators.length === 0;
                    })() ? (
                      <Box sx={{ m: 0, p: 0 }}>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          No indicator with <b>Signal used for = Forward</b> is available. Please add at least one indicator and set its{" "}
                          <b>Signal used for</b> setting to <b>Forward</b>.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nCaSupResUsedForValueInjection */}
                    {/* ---------------------------------------------------------------------- */}
                    {values.nType === "5" &&
                    (() => {
                      const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                      return forwardChartAnalyzers.length > 0;
                    })() ? (
                      <Box>
                        <SmartParameter
                          value="Used chart analyzer"
                          helpReference="HandleTakeProfitDynamics"
                          helpText=""
                          helpLink=""
                          fieldName="nCaSupResUsedForValueInjection"
                          touched={touched}
                          errors={errors}
                        />
                        <FormControl fullWidth>
                          <Select
                            {...getFieldProps("nCaSupResUsedForValueInjection")}
                            input={<ModalContentOutlinedInput />}
                            error={Boolean(touched.nCaSupResUsedForValueInjection && errors.nCaSupResUsedForValueInjection)}
                          >
                            <ModalContentMenuItem value="0">
                              <Box>
                                <Typography variant="contentSmall">Please select ...</Typography>
                              </Box>
                            </ModalContentMenuItem>
                            {chartAnalyzers
                              ?.filter((chartAnalyzer) => chartAnalyzer.config?.nSignalUsedFor === "2")
                              .map((chartAnalyzer) => (
                                <ModalContentMenuItem key={chartAnalyzer.id} value={chartAnalyzer.id.toString()}>
                                  <Box>
                                    <Typography variant="contentSmall">
                                      {chartAnalyzer.name} ({chartAnalyzer.subtitle})
                                    </Typography>
                                  </Box>
                                </ModalContentMenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    {values.nType === "5" &&
                    (() => {
                      const forwardChartAnalyzers = chartAnalyzers?.filter((ca) => ca.config?.nSignalUsedFor === "2") || [];
                      return forwardChartAnalyzers.length === 0;
                    })() ? (
                      <Box sx={{ m: 0, p: 0 }}>
                        <ModalAlertError
                          sx={{
                            m: "0 !important",
                          }}
                          severity="error"
                        >
                          No chart analyzer with <b>Signal used for = Forward</b> is available. Please add at least one chart analyzer and set its{" "}
                          <b>Signal used for</b> setting to <b>Forward</b>.
                        </ModalAlertError>
                      </Box>
                    ) : null}

                    {values.nType === "5" ? (
                      <>
                        {professionalMode === true ? (
                          <>
                                                      {/* ---------------------------------------------------------------------- */}
                                                      {/* Divider */}
                                                      {/* ---------------------------------------------------------------------- */}
                                                      <Box>
                                                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                                                      </Box>
                          
                                                      {/* ---------------------------------------------------------------------- */}
                                                      {/* Grid */}
                                                      {/* ---------------------------------------------------------------------- */}
                                                      <Box
                                                        sx={{
                                                          display: "grid",
                                                          gridTemplateColumns: "1fr 1fr",
                                                          gap: 2,
                                                        }}
                                                      >
                                                        {/* ---------------------------------------------------------------------- */}
                                                        {/* Parameter configuration */}
                                                        {/* nFixPipsMin */}
                                                        {/* ---------------------------------------------------------------------- */}
                                                        <Box>
                                                          <SmartParameter
                                                            value="Support/resistance fix pips minimum"
                                                            helpReference="HandleTakeProfitDynamics"
                                                            helpText=""
                                                            helpLink=""
                                                            fieldName="nCaSupResFixPipsMin"
                                                            touched={touched}
                                                            errors={errors}
                                                          />
                                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                            <ModalContentSwitch
                                                              sx={{ my: "12px" }}
                                                              checked={values.bCaSupResFixPipsMin === true}
                                                              onChange={(event) => {
                                                                setFieldValue("bCaSupResFixPipsMin", event.target.checked);
                                                              }}
                                                            />
                                                            {values.bCaSupResFixPipsMin === true ? (
                                                              <SmartNumberInput textFieldName="nCaSupResFixPipsMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                                            ) : null}
                                                          </Box>
                                                        </Box>
                          
                                                        {/* ---------------------------------------------------------------------- */}
                                                        {/* Parameter configuration */}
                                                        {/* dCaSupResRateOfChangeMin */}
                                                        {/* ---------------------------------------------------------------------- */}
                                                        <Box>
                                                          <SmartParameter
                                                            value="Support/resistance rate of change minimum"
                                                            helpReference="HandleTakeProfitDynamics"
                                                            helpText=""
                                                            helpLink=""
                                                            fieldName="dCaSupResRateOfChangeMin"
                                                            touched={touched}
                                                            errors={errors}
                                                          />
                                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                            <ModalContentSwitch
                                                              sx={{ my: "12px" }}
                                                              checked={values.bCaSupResRateOfChangeMin === true}
                                                              onChange={(event) => {
                                                                setFieldValue("bCaSupResRateOfChangeMin", event.target.checked);
                                                              }}
                                                            />
                                                            {values.bCaSupResRateOfChangeMin === true ? (
                                                              <SmartNumberInput textFieldName="dCaSupResRateOfChangeMin" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                                            ) : null}
                                                          </Box>
                                                        </Box>
                                                      </Box>
                          
                                                      {/* ---------------------------------------------------------------------- */}
                                                      {/* Divider */}
                                                      {/* ---------------------------------------------------------------------- */}
                                                      <Box>
                                                        <Divider sx={{ mt: 2, mb: 2, borderColor: "#C8C8C8" }} />
                                                      </Box>
                          
                                                      {/* ---------------------------------------------------------------------- */}
                                                      {/* Grid */}
                                                      {/* ---------------------------------------------------------------------- */}
                                                      <Box
                                                        sx={{
                                                          display: "grid",
                                                          gridTemplateColumns: "1fr 1fr",
                                                          gap: 2,
                                                        }}
                                                      >
                                                        {/* ---------------------------------------------------------------------- */}
                                                        {/* Parameter configuration */}
                                                        {/* nFixPipsMax */}
                                                        {/* ---------------------------------------------------------------------- */}
                                                        <Box>
                                                          <SmartParameter
                                                            value="Support/resistance fix pips maximum"
                                                            helpReference="HandleTakeProfitDynamics"
                                                            helpText=""
                                                            helpLink=""
                                                            fieldName="nCaSupResFixPipsMax"
                                                            touched={touched}
                                                            errors={errors}
                                                          />
                                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                            <ModalContentSwitch
                                                              sx={{ my: "12px" }}
                                                              checked={values.bCaSupResFixPipsMax === true}
                                                              onChange={(event) => {
                                                                setFieldValue("bCaSupResFixPipsMax", event.target.checked);
                                                              }}
                                                            />
                                                            {values.bCaSupResFixPipsMax === true ? (
                                                              <SmartNumberInput textFieldName="nCaSupResFixPipsMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                                            ) : null}
                                                          </Box>
                                                        </Box>
                          
                                                        {/* ---------------------------------------------------------------------- */}
                                                        {/* Parameter configuration */}
                                                        {/* dCaSupResRateOfChangeMax */}
                                                        {/* ---------------------------------------------------------------------- */}
                                                        <Box>
                                                          <SmartParameter
                                                            value="Support/resistance rate of change maximum"
                                                            helpReference="HandleTakeProfitDynamics"
                                                            helpText=""
                                                            helpLink=""
                                                            fieldName="dCaSupResRateOfChangeMax"
                                                            touched={touched}
                                                            errors={errors}
                                                          />
                                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                            <ModalContentSwitch
                                                              sx={{ my: "12px" }}
                                                              checked={values.bCaSupResRateOfChangeMax === true}
                                                              onChange={(event) => {
                                                                setFieldValue("bCaSupResRateOfChangeMax", event.target.checked);
                                                              }}
                                                            />
                                                            {values.bCaSupResRateOfChangeMax === true ? (
                                                              <SmartNumberInput textFieldName="dCaSupResRateOfChangeMax" defaultLimits={DEFAULT_LIMITS} formik={formik} />
                                                            ) : null}
                                                          </Box>
                                                        </Box>
                                                      </Box>
                                                    </>
                        ) : null}
                      </>
                    ) : null}
                  </Stack>
                </DialogContent>

                <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                  <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
                </Box>

                <DialogActions
                  sx={{
                    background: "#F2F2F2",
                    p: 2,
                    pt: 0,
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleReset}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Reset
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      disabled={saveAndCloseDisabled}
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Save
                    </ModalActionsLoadingButton>

                    <ModalActionsLoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      onClick={handleClose}
                      loading={isSubmitting}
                      sx={{ mt: 1 }}
                    >
                      Close
                    </ModalActionsLoadingButton>
                  </Box>
                </DialogActions>
              </FormikProvider>
            </>
          }
        />
      </>
    );
  }
);
